// ── 2 Lizenz ── das Formular, mit den Benutzer-Lizenzen (docs/11 8e).

import type { Catalog, Tenant } from "../../api/types";
import { eur } from "../../lib/format";
import { FEATURE } from "../../lib/labels";
import { Link } from "../../router/router";
import { Note } from "../../ui/badge";
import { Check, Field, Input } from "../../ui/field";
import { SelectMenu } from "../../ui/select-menu";
import { defaultSeatOptions, SEAT_MODE, seatSums, type LicenseState } from "./license";
import { fieldError, setter, Warnings } from "./shared";
import type { StepFormProps } from "./types";

export function LicenseForm({ tenant: t, catalog: cat, state, setState, check, host }: StepFormProps<LicenseState>) {
  const set = setter(setState);
  const err = (k: string) => fieldError(check, k);
  const num = (k: "max_users" | "max_workspaces" | "memory_mb" | "cpus") => (e: { target: { value: string } }) => set(k)(e.target.value);
  const toggleFeature = (f: string, on: boolean) => setState((s) => ({ ...s, features: on ? [...s.features.filter((x) => x !== f), f] : s.features.filter((x) => x !== f) }));
  return (
    <>
      <div className="pf-grid">
        <Field label="Benutzer (leer = unbegrenzt)"><Input name="max_users" type="number" min={0} value={state.max_users} onChange={num("max_users")} /></Field>
        <Field label="Arbeitsbereiche (leer = unbegrenzt)"><Input name="max_workspaces" type="number" min={0} value={state.max_workspaces} onChange={num("max_workspaces")} /></Field>
        <Field label="Speicher je Arbeitsbereich (MB)"><Input name="memory_mb" type="number" min={0} step={256} value={state.memory_mb} onChange={num("memory_mb")} /></Field>
        <Field label="CPUs je Arbeitsbereich"><Input name="cpus" type="number" min={0} step={0.5} value={state.cpus} onChange={num("cpus")} /></Field>
        <Field label="Gültig bis (einschließlich)" error={err("valid_until")} hint="Leer = unbefristet. Eine auslaufende Lizenz sperrt niemanden mitten im Turn aus, sie verhindert Neues.">
          <Input name="valid_until" type="date" value={state.valid_until} invalid={!!err("valid_until")} onChange={(e) => set("valid_until")(e.target.value)} />
        </Field>
      </div>
      <div className="pf-field">
        <span className="pf-field__label">Features</span>
        <div className="pf-row">
          {cat.features.map((f) => <Check key={f} name="features" value={f} checked={state.features.includes(f)} onChange={(on) => toggleFeature(f, on)} label={FEATURE[f] ?? f} />)}
          <Check name="all_features" checked={state.all_features} onChange={set("all_features")} label="alle, auch künftige" />
        </div>
        {err("features") && <span className="pf-field__error">{err("features")}</span>}
      </div>
      <div className="pf-row">
        <Check name="watchers" checked={state.watchers} onChange={set("watchers")} label="Routinen" />
        <Check name="voice" checked={state.voice} onChange={set("voice")} label="Telefonieren" />
        <Check name="custom_mcp" checked={state.custom_mcp} onChange={set("custom_mcp")} label="Eigene MCP-Server" />
      </div>
      <Seats tenant={t} cat={cat} state={state} set={set} err={err} />
      {host && <Note>{`Host: ${host.cpus} vCPU, ${Math.round(host.memory_mb / 1024)} GB, ${host.containers_running} laufende Container. Arbeitsbereiche aller Mandanten teilen ihn.`}</Note>}
      <Warnings check={check} />
    </>
  );
}

function Seats({ tenant: t, cat, state, set, err }: {
  tenant: Tenant;
  cat: Catalog;
  state: LicenseState;
  set: ReturnType<typeof setter<LicenseState>>;
  err: (k: string) => string;
}) {
  const tpls = cat.license_templates ?? [];
  const used = new Map((t.last_status?.seats ?? []).map((x) => [x.key, x.used]));
  const sums = seatSums(state.seats, tpls);
  return (
    <div className="pf-field pf-field--wide">
      <span className="pf-field__label">Benutzer-Lizenzen</span>
      <SelectMenu name="seat_mode" value={state.seat_mode} options={SEAT_MODE} onChange={set("seat_mode")} ariaLabel="Benutzer-Lizenzen" />
      {/* Versteckt, nicht entfernt: Die Plätze gehen auch bei „Aus" mit (collectSeats). */}
      <div className="pf-seats" hidden={state.seat_mode === "off"}>
        {tpls.length ? tpls.map((tpl) => (
          <div className="pf-seat" key={tpl.id}>
            <span className="pf-seat__name">
              <strong>{tpl.name}</strong>
              {used.has(tpl.key) && <> <span className="pf-sub">· {used.get(tpl.key)} vergeben</span></>}
              <span className="pf-sub"> · {tpl.price_eur_month === null ? "kein Preis" : `${eur(tpl.price_eur_month)} je Benutzer/Monat`}</span>
            </span>
            <Input
              name={`seat_${tpl.id}`} type="number" min={0} step={1} placeholder="Plätze" aria-label={`Plätze ${tpl.name}`}
              value={state.seats[String(tpl.id)] ?? ""}
              onChange={(e) => set("seats")({ ...state.seats, [String(tpl.id)]: e.target.value })}
            />
            <span className="pf-seat__price pf-sub">{sums.per[String(tpl.id)]}</span>
          </div>
        )) : <Note>Noch keine Lizenz-Vorlage. <Link to="/licenses/new">Lizenz anlegen</Link></Note>}
        {err("seats") && <span className="pf-field__error">{err("seats")}</span>}
        <div className="pf-row" hidden={state.seat_mode !== "auto"}>
          <Field label="Standard-Lizenz für neue Benutzer" error={err("default_seat")}>
            <SelectMenu name="default_seat" value={state.default_seat} options={defaultSeatOptions(tpls)} onChange={set("default_seat")} />
          </Field>
        </div>
        <div className="pf-sub">{sums.total}</div>
      </div>
    </div>
  );
}
